import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const agentActionErrorTypeValues = [
  "AGENT_ACTION_NOT_FOUND",
  "AGENT_ACTION_TARGET_INELIGIBLE",
  "AGENT_ACTION_NOT_OPEN",
  "AGENT_ACTION_EXPIRED",
  "AGENT_ACTION_STALE",
  "AGENT_ACTION_PAYLOAD_INVALID",
  "AGENT_ACTION_REASON_NOT_VISIBLE",
  "AGENT_ACTION_REQUEST_EMIT_FAILED",
  "AGENT_ACTION_EXECUTION_LOST",
  "AGENT_ACTION_EXECUTION_FAILED",
  "AGENT_ACTION_TOO_MANY_TASKS",
  "AGENT_ACTION_SLOT_RACE_LOST",
] as const;
const agentActionErrorTypeSchema = z.enum(agentActionErrorTypeValues);
const AgentActionErrorType = agentActionErrorTypeSchema.enum;
type AgentActionErrorType = z.infer<typeof agentActionErrorTypeSchema>;

export type AgentActionDomainErrors =
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_NOT_FOUND;
      data: { taskId?: string; agentActionId?: string; conversationId?: string };
    }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_TARGET_INELIGIBLE;
      data: { taskId: string } | { conversationId: string };
    }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_NOT_OPEN;
      data: { agentActionId: string; currentStatus: string };
    }
  | { errorType: typeof AgentActionErrorType.AGENT_ACTION_EXPIRED; data: { agentActionId: string } }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_STALE;
      data: { agentActionId: string; currentStatus: string };
    }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_PAYLOAD_INVALID;
      data: { agentActionId: string; rejectionCode: string };
    }
  | { errorType: typeof AgentActionErrorType.AGENT_ACTION_REASON_NOT_VISIBLE; data: { agentActionId: string } }
  | { errorType: typeof AgentActionErrorType.AGENT_ACTION_REQUEST_EMIT_FAILED; data: { requestId: string } }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_EXECUTION_LOST;
      data: { agentActionId: string; taskId: string | null };
    }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_EXECUTION_FAILED;
      data: { agentActionId: string; failureCode: string };
    }
  | { errorType: typeof AgentActionErrorType.AGENT_ACTION_TOO_MANY_TASKS; data: { taskCount: number; max: number } }
  | {
      errorType: typeof AgentActionErrorType.AGENT_ACTION_SLOT_RACE_LOST;
      data: { requestId: string; openSlotKey: string };
    };

export const agentActionErrorDetails: Record<AgentActionErrorType, ErrorDetails> = {
  AGENT_ACTION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Suggested action or its task was not found",
  },
  AGENT_ACTION_TARGET_INELIGIBLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "This task or thread cannot receive suggested actions",
  },
  AGENT_ACTION_NOT_OPEN: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suggested action is no longer open",
  },
  AGENT_ACTION_EXPIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suggested action has expired",
  },
  AGENT_ACTION_STALE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suggested action changed since it was read",
  },
  AGENT_ACTION_PAYLOAD_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suggested action details are invalid",
  },
  AGENT_ACTION_REASON_NOT_VISIBLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Suggested attempt reason is not available to this user",
  },
  AGENT_ACTION_REQUEST_EMIT_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Suggestion request could not be sent",
  },
  AGENT_ACTION_EXECUTION_LOST: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Accepted suggested action never executed",
  },
  AGENT_ACTION_EXECUTION_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Accepted suggested action failed to execute",
  },
  AGENT_ACTION_TOO_MANY_TASKS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Too many tasks requested for suggested actions at once",
  },
  AGENT_ACTION_SLOT_RACE_LOST: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Suggested action lost its slot to a concurrent write during ingestion",
  },
};
